import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Descriptions, Divider, Drawer, Empty, Form, Input, Menu, message, Modal, Row, Select, Space, Spin, Tag, Tooltip, Table, Radio, List, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, CheckCircleTwoTone, CloseCircleTwoTone, ExclamationCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import reqwest from 'reqwest';
import config from '../../config';
import { getToken, formatDoseByUnit } from '../../Utils/common';

const { TextArea } = Input;

// UI labels con acentos
const VALORACION_LABELS = {
  CLINICA: 'Clínica',
  ENFERMERIA: 'Enfermería',
  NUTRICIONAL: 'Nutricional',
  PSIQUIATRICA: 'Psiquiátrica',
  FUNCIONAL: 'Funcional',
  KINESICA: 'Kinésica',
  PSICOLOGICA: 'Psicológica',
  SOCIAL: 'Social',
};

const PAI_LABELS = {
  OBJETIVO_GENERAL: 'Objetivo General',
  CLINICA: 'Clínica',
  ENFERMERIA: 'Enfermería',
  NUTRICIONAL: 'Nutricional',
  PSIQUIATRICA: 'Psiquiátrica',
  OCUPACIONAL_RECREACION: 'Ocupacional/Recreación',
  KINESICA: 'Kinésica',
  PSICOLOGICA: 'Psicológica',
  SOCIAL: 'Social',
};

function JsonEditorDrawer({ visible, onClose, title, initialValue, onSave, disabled }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    form.setFieldsValue({ json: initialValue ? JSON.stringify(initialValue, null, 2) : '{\n\n}' });
  }, [initialValue]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let parsed = {};
      if (values.json && values.json.trim()) {
        try { parsed = JSON.parse(values.json); }
        catch (e) { message.error('JSON inválido'); return; }
      }
      setSaving(true);
      await onSave(parsed);
      setSaving(false);
      onClose(true);
    } catch (e) {
      setSaving(false);
    }
  };

  return (
    <Drawer width={600} title={title} onClose={() => onClose(false)} visible={visible} destroyOnClose
            extra={<Space><Button onClick={() => onClose(false)}>Cancelar</Button><Button type="primary" onClick={handleSubmit} disabled={disabled} loading={saving}>Guardar</Button></Space>}>
      <Form form={form} layout="vertical">
        <Form.Item name="json" label="Datos (JSON)" rules={[{ required: true, message: 'Complete los datos'}]}>
          <TextArea autoSize={{ minRows: 16 }} disabled={disabled} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

function ClinicalValuationDrawer({
  visible,
  onClose,
  vgi,
  record,
  onSave,
  disabled,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Formateador para valores que pueden venir como objeto desde el prefill (evitar [object Object])
  // Regla general: unir en orden las claves relevantes si existen.
  // - Para ppr y puntos_ureterales: "resultado/localizacion" (si hay localizacion)
  // - Para sistema_digestivo, sistema_genitourinario, aparato_respiratorio, auscultacion_cardiaca: "resultado - detalle" (si hay detalle)
  const formatExamValue = (val, kind) => {
    if (val == null) return '';
    if (typeof val === 'string') return val;
    if (typeof val !== 'object') return String(val || '');
    const v = val || {};
    const pick = (k) => (v[k] == null ? '' : (typeof v[k] === 'object' ? '' : String(v[k])));
    if (kind === 'ppr' || kind === 'puntos_ureterales') {
      const r = pick('resultado');
      const loc = pick('localizacion');
      return [r, loc].filter(Boolean).join('/');
    }
    if (kind === 'sistema_digestivo' || kind === 'sistema_genitourinario' || kind === 'aparato_respiratorio' || kind === 'auscultacion_cardiaca') {
      const r = pick('resultado');
      const det = pick('detalle');
      return [r, det].filter(Boolean).join(' - ');
    }
    // genérico: concatenar valores primitivos en orden de las claves más comunes
    const keys = ['resultado', 'detalle', 'localizacion', 'value'];
    const parts = keys.map(pick).filter(Boolean);
    if (parts.length) return parts.join(' ');
    try { return JSON.stringify(val); } catch (e) { return String(val); }
  };

  const setEFFields = (ef = {}) => {
    form.setFieldsValue({
      ef_ta: ef.ta || ef.TA || '',
      ef_fc: ef.fc || ef.F_C || ef.Fc || ef.F_Card || '',
      ef_temperatura: ef.temperatura || ef.temp || '',
      ef_hgt: ef.hgt || ef.HGT || '',
      ef_sat: ef.sat || ef.SAT || ef.saturacion || ef.saturación || '',
      ef_sistema_digestivo: formatExamValue(ef.sistema_digestivo || ef.digestivo || '', 'sistema_digestivo'),
      ef_sistema_genitourinario: formatExamValue(ef.sistema_genitourinario || ef.genitourinario || '', 'sistema_genitourinario'),
      ef_puntos_uretrales: formatExamValue(ef.puntos_uretrales || ef.p_uretrales || '', 'puntos_ureterales'),
      ef_ppr: formatExamValue(ef.ppr || '', 'ppr'),
      ef_aparato_respiratorio: formatExamValue(ef.aparato_respiratorio || ef.respiratorio || '', 'aparato_respiratorio'),
      ef_auscultacion_cardiaca: formatExamValue(ef.auscultacion_cardiaca || ef.auscultación_cardiaca || ef.cardiaca || '', 'auscultacion_cardiaca'),
    });
  };

  // Prefill from last ECM when creating (no existing record)
  useEffect(() => {
    form.resetFields();
    // Initial values from existing record (editing)
    if (record && record.data) {
      form.setFieldsValue({
        descripcion: record.data.descripcion || '',
        diagnosticos: record.data.diagnosticos || '',
      });
      // soporte retrocompatible: si examen_fisico es string, lo dejamos sin mapear; si es objeto, cargamos campos
      if (record.data.examen_fisico && typeof record.data.examen_fisico === 'object') {
        setEFFields(record.data.examen_fisico);
      } else if (typeof record.data.examen_fisico === 'string') {
        // no sabemos el formato, lo ignoramos en campos específicos
      }
      return;
    }
    // If creating, fetch prefill from backend for the last ECM of the patient
    if (vgi?.pacient && visible) {
      setLoading(true);
      // Prefill endpoint (server prepares last ECM data)
      reqwest({
        url: `${config.api_url}/patientprogress/prefill/?patient_id=${vgi.pacient}&type=ECM`,
        method: 'get',
        type: 'json',
        headers: { 'Authorization': `Token ${getToken()}` },
      })
        .then((resp) => {
          // Prefill desde endpoint dedicado
          const pf = resp && resp.data ? resp.data : {};
          const efRaw = pf.examen_fisico || pf.physical_exam || pf.ef || null;
          const diagnosticos = pf.diagnostico || pf.diagnosticos || pf.diagnosis || '';
          form.setFieldsValue({
            descripcion: '',
            diagnosticos: diagnosticos,
          });
          // Soportar nuevos campos al mismo nivel del prefill (sistema_digestivo, etc.)
          const extraEF = {
            sistema_digestivo: formatExamValue(pf.sistema_digestivo, 'sistema_digestivo') || undefined,
            sistema_genitourinario: formatExamValue(pf.sistema_genitourinario, 'sistema_genitourinario') || undefined,
            // aceptar ambas variantes por compatibilidad: ureterales (correcto) y uretrales (legado)
            puntos_uretrales: formatExamValue(pf.puntos_ureterales || pf.puntos_uretrales, 'puntos_ureterales') || undefined,
            ppr: (efRaw && typeof efRaw === 'object' && (efRaw.ppr || efRaw.PPR))
              ? formatExamValue(efRaw.ppr || efRaw.PPR, 'ppr')
              : formatExamValue(pf.ppr || pf.PPR, 'ppr') || undefined,
            aparato_respiratorio: formatExamValue(pf.aparato_respiratorio, 'aparato_respiratorio') || undefined,
            auscultacion_cardiaca: formatExamValue(pf.auscultacion_cardiaca || pf.auscultación_cardiaca, 'auscultacion_cardiaca') || undefined,
          };
          if (efRaw && typeof efRaw === 'object') {
            setEFFields({ ...efRaw, ...extraEF });
          } else if (typeof efRaw === 'string') {
            // Intento simple de extracción por patrones comunes (TA, FC, etc.)
            const text = efRaw;
            const pick = (labelPatterns) => {
              const patterns = Array.isArray(labelPatterns) ? labelPatterns : [labelPatterns];
              for (const p of patterns) {
                const re = new RegExp(`${p}\s*[:.-]?\s*([^\n;]+)`, 'i');
                const m = text.match(re);
                if (m) return m[1].trim();
              }
              return '';
            };
            setEFFields({
              ta: pick(['TA', 'Tensión arterial', 'Tension arterial']),
              fc: pick(['F.C', 'FC', 'Frecuencia cardiaca', 'Frecuencia cardíaca']),
              temperatura: pick(['Temperatura', 'Temp']),
              hgt: pick(['HGT', 'Glucemia'] ),
              sat: pick(['SAT', 'Saturación', 'Saturacion']),
              sistema_digestivo: extraEF.sistema_digestivo !== undefined ? extraEF.sistema_digestivo : pick(['Sistema Digestivo', 'Digestivo']),
              sistema_genitourinario: extraEF.sistema_genitourinario !== undefined ? extraEF.sistema_genitourinario : pick(['Sistema Genitourinario', 'Genitourinario']),
              puntos_uretrales: extraEF.puntos_uretrales !== undefined ? extraEF.puntos_uretrales : pick(['Puntos Uretrales']),
              ppr: extraEF.ppr !== undefined ? extraEF.ppr : pick(['PPR']),
              aparato_respiratorio: extraEF.aparato_respiratorio !== undefined ? extraEF.aparato_respiratorio : pick(['Aparato Respiratorio', 'Respiratorio']),
              auscultacion_cardiaca: extraEF.auscultacion_cardiaca !== undefined ? extraEF.auscultacion_cardiaca : pick(['Auscultación Cardíaca', 'Auscultacion Cardiaca', 'Cardiaca']),
            });
          }
        })
        .always(() => setLoading(false));
    }
  }, [record, vgi?.pacient, visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const examen_fisico = {
        ta: values.ef_ta || '',
        fc: values.ef_fc || '',
        temperatura: values.ef_temperatura || '',
        hgt: values.ef_hgt || '',
        sat: values.ef_sat || '',
        sistema_digestivo: values.ef_sistema_digestivo || '',
        sistema_genitourinario: values.ef_sistema_genitourinario || '',
        puntos_uretrales: values.ef_puntos_uretrales || '',
        ppr: values.ef_ppr || '',
        aparato_respiratorio: values.ef_aparato_respiratorio || '',
        auscultacion_cardiaca: values.ef_auscultacion_cardiaca || '',
      };
      const data = {
        descripcion: values.descripcion || '',
        examen_fisico,
        diagnosticos: values.diagnosticos || '',
      };
      await onSave(data);
      onClose(true);
    } catch (e) {
      // validation error handled by antd
    }
  };

  return (
    <Drawer
      width={700}
      title={record ? 'Editar valoración - Clínica' : 'Nueva valoración - Clínica'}
      visible={visible}
      onClose={() => onClose(false)}
      destroyOnClose
      extra={(
        <Space>
          <Button onClick={() => onClose(false)}>Cancelar</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading} disabled={disabled}>Guardar</Button>
        </Space>
      )}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Form.Item name="descripcion" label="Descripción" rules={[{ required: true, message: 'Ingrese una descripción' }]}>
            <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
          </Form.Item>

          <Divider orientation="left">Examen Físico</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ef_ta" label="TA">
                <Input placeholder="Ej: 120/80" disabled={disabled} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ef_fc" label="F.C.">
                <Input placeholder="Ej: 78 lpm" disabled={disabled} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ef_temperatura" label="Temperatura">
                <Input placeholder="Ej: 36.7 °C" disabled={disabled} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ef_hgt" label="HGT">
                <Input placeholder="Ej: 98 mg/dl" disabled={disabled} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ef_sat" label="SAT">
                <Input placeholder="Ej: 97%" disabled={disabled} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="ef_sistema_digestivo" label="Sistema Digestivo">
            <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="ef_sistema_genitourinario" label="Sistema Genitourinario">
            <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="ef_puntos_uretrales" label="Puntos Uretrales">
            <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="ef_ppr" label="PPR">
            <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="ef_aparato_respiratorio" label="Aparato Respiratorio">
            <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="ef_auscultacion_cardiaca" label="Auscultación Cardíaca">
            <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
          </Form.Item>

          <Form.Item name="diagnosticos" label="Diagnósticos Clínicos Actuales">
            <TextArea autoSize={{ minRows: 4 }} placeholder="Se precompleta desde la última ECM (si existe)" disabled={disabled} />
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
}

function FunctionalValuationDrawer({ visible, onClose, vgi, record, onSave, disabled }) {
  const [form] = Form.useForm();

  useEffect(() => {
    form.resetFields();
    if (record && record.data) {
      const d = record.data || {};
      form.setFieldsValue({
        aspectos_generales: d.aspectos_generales || d.aspecto_generales || '',
        escala_lawton_brody: d.escala_lawton_brody || d.lawton_brody || '',
      });
    }
  }, [record, visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        aspectos_generales: values.aspectos_generales || '',
        escala_lawton_brody: values.escala_lawton_brody || '',
      };
      await onSave(payload);
      onClose(true);
    } catch (e) {
      // handled by antd
    }
  };

  return (
    <Drawer
      width={640}
      title={record ? 'Valoración Funcional' : 'Nueva valoración Funcional'}
      onClose={() => onClose(false)}
      visible={visible}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={() => onClose(false)}>Cancelar</Button>
          <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item name="aspectos_generales" label="Aspectos generales">
          <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
        </Form.Item>
        <Form.Item name="escala_lawton_brody" label="Escala de Lawton y Brody">
          <TextArea autoSize={{ minRows: 2 }} placeholder="Ej: 6/8 - Comentarios opcionales" disabled={disabled} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

function KinesicValuationDrawer({ visible, onClose, vgi, record, onSave, disabled }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    form.resetFields();
    // Si estamos editando, cargar valores existentes
    if (record && record.data) {
      const d = record.data || {};
      form.setFieldsValue({
        aspectos_generales: d.aspectos_generales || d.aspecto_generales || '',
        tinetti: d.tinetti || d.escala_tinetti || '',
        katz: d.katz || d.indice_katz || '',
      });
      return;
    }
    // Si estamos creando, prefill desde última Admisión Kinésica (AKI)
    if (vgi?.pacient && visible) {
      setLoading(true);
      reqwest({
        url: `${config.api_url}/patientprogress/prefill/?patient_id=${vgi.pacient}&type=AKI`,
        method: 'get',
        type: 'json',
        headers: { 'Authorization': `Token ${getToken()}` },
      }).then((resp) => {
        const pf = (resp && resp.data) ? resp.data : {};
        form.setFieldsValue({
          aspectos_generales: '',
          tinetti: pf.tinetti || pf.aki_clasificacion_riesgo || pf.clasificacion_riesgo || '',
          katz: pf.katz || pf.aki_indice_katz || pf.indice_katz || '',
        });
      }).always(() => setLoading(false));
    }
  }, [record, vgi?.pacient, visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        aspectos_generales: values.aspectos_generales || '',
        tinetti: values.tinetti || '',
        katz: values.katz || '',
      };
      await onSave(payload);
      onClose(true);
    } catch (e) {
      // validation error
    }
  };

  return (
    <Drawer
      width={640}
      title={record ? 'Valoración Kinésica' : 'Nueva valoración Kinésica'}
      onClose={() => onClose(false)}
      visible={visible}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={() => onClose(false)}>Cancelar</Button>
          <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Form.Item name="aspectos_generales" label="Aspectos generales">
            <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="tinetti" label="Escala Tinetti">
                <Input placeholder="Ej: Bajo/Moderado/Alto riesgo o puntaje" disabled={disabled} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="katz" label="Índice de Katz">
                <Input placeholder="Ej: A/B/C... o puntaje" disabled={disabled} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Drawer>
  );
}

function NursingValuationDrawer({ visible, onClose, vgi, record, onSave, disabled }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    form.resetFields();
    // Si estamos editando, cargar valores existentes
    if (record && record.data) {
      const d = record.data || {};
      form.setFieldsValue({
        aspectos_generales: d.aspectos_generales || d.aspecto_generales || '',
        piel: d.piel || '',
        audicion: d.audicion || d.audición || '',
        alimentacion: d.alimentacion || d.alimentación || '',
        denticion: d.denticion || d.dentición || '',
        esfinteres: d.esfinteres || d.esfínteres || '',
        vision: d.vision || d.visión || '',
        escala_riesgo_ulceras_por_presion: d.escala_riesgo_ulceras_por_presion || d.escala_riesgo_de_ulceras_por_presion || d.escala_ulceras || d.riesgo_ulceras || '',
        deambulacion: d.deambulacion || d.deambulación || '',
      });
      return;
    }
    // Si estamos creando, prefill desde última PAE
    if (vgi?.pacient && visible) {
      setLoading(true);
      reqwest({
        url: `${config.api_url}/patientprogress/prefill/?patient_id=${vgi.pacient}&type=PAE`,
        method: 'get',
        type: 'json',
        headers: { 'Authorization': `Token ${getToken()}` },
      }).then((resp) => {
        const pf = (resp && resp.data) ? resp.data : {};
        form.setFieldsValue({
          aspectos_generales: '',
          piel: pf.piel || '',
          audicion: pf.audicion || pf['audición'] || '',
          alimentacion: pf.alimentacion || pf['alimentación'] || '',
          denticion: pf.denticion || pf['dentición'] || '',
          esfinteres: pf.esfinteres || pf['esfínteres'] || '',
          vision: pf.vision || pf['visión'] || '',
          escala_riesgo_ulceras_por_presion: pf.escala_riesgo_ulceras_por_presion || pf.escala_riesgo_de_ulceras_por_presion || pf.escala_ulceras || pf.riesgo_ulceras || '',
          deambulacion: pf.deambulacion || pf['deambulación'] || '',
        });
      }).always(() => setLoading(false));
    }
  }, [record, vgi?.pacient, visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        aspectos_generales: values.aspectos_generales || '',
        piel: values.piel || '',
        audicion: values.audicion || '',
        alimentacion: values.alimentacion || '',
        denticion: values.denticion || '',
        esfinteres: values.esfinteres || '',
        vision: values.vision || '',
        escala_riesgo_ulceras_por_presion: values.escala_riesgo_ulceras_por_presion || '',
        deambulacion: values.deambulacion || '',
      };
      await onSave(payload);
      onClose(true);
    } catch (e) {
      // validation error
    }
  };

  return (
    <Drawer
      width={720}
      title={record ? 'Valoración de Enfermería' : 'Nueva valoración de Enfermería'}
      onClose={() => onClose(false)}
      visible={visible}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={() => onClose(false)}>Cancelar</Button>
          <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Form.Item name="aspectos_generales" label="Aspectos Generales">
            <TextArea autoSize={{ minRows: 3 }} disabled={disabled} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="piel" label="Piel">
                <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="audicion" label="Audición">
                <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="alimentacion" label="Alimentación">
                <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="denticion" label="Dentición">
                <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="esfinteres" label="Esfínteres">
                <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="vision" label="Visión">
                <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="escala_riesgo_ulceras_por_presion" label="Escala de Riesgo de Úlceras por Presión">
            <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="deambulacion" label="Deambulación">
            <TextArea autoSize={{ minRows: 2 }} disabled={disabled} />
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
}

function NutritionalValuationDrawer({ visible, onClose, vgi, record, onSave, disabled }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    form.resetFields();
    // Si estamos editando, cargar valores existentes
    if (record && record.data) {
      const d = record.data || {};
      form.setFieldsValue({
        bmi: d.bmi ?? '',
        mna: d.mna ?? '',
        dieta: d.dieta ?? '',
        comentarios: d.comentarios ?? '',
      });
      return;
    }
    // Si estamos creando, prefill desde última Evolución Nutricional (NUT) y/o Admisión Nutrición (ANU)
    if (vgi?.pacient && visible) {
      setLoading(true);
      reqwest({
        url: `${config.api_url}/patientprogress/prefill/?patient_id=${vgi.pacient}&type=NUT`,
        method: 'get',
        type: 'json',
        headers: { 'Authorization': `Token ${getToken()}` },
      }).then((resp) => {
        const pf = (resp && resp.data) ? resp.data : {};
        form.setFieldsValue({
          bmi: pf.bmi ?? '',
          mna: pf.mna ?? '',
          dieta: pf.dieta ?? '',
          comentarios: '',
        });
      }).always(() => setLoading(false));
    }
  }, [record, vgi?.pacient, visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        bmi: values.bmi ?? '',
        mna: values.mna ?? '',
        dieta: values.dieta ?? '',
        comentarios: values.comentarios ?? '',
      };
      await onSave(payload);
      onClose(true);
    } catch (e) {
      // validation error
    }
  };

  return (
    <Drawer
      width={620}
      title={record ? 'Valoración Nutricional' : 'Nueva valoración Nutricional'}
      onClose={() => onClose(false)}
      visible={visible}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={() => onClose(false)}>Cancelar</Button>
          <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bmi" label="BMI">
                <Input placeholder="Ej: 22.5" disabled={disabled} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mna" label="Estado Nutricional (MNA)">
                <Input placeholder="Ej: 12-14 Estado nutricional normal" disabled={disabled} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="dieta" label="Dieta">
            <TextArea autoSize={{ minRows: 3 }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="comentarios" label="Comentarios">
            <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
}

function PsychiatricValuationDrawer({ visible, onClose, vgi, record, onSave, disabled }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    form.resetFields();
    // Editar: cargar valores existentes
    if (record && record.data) {
      const d = record.data || {};
      form.setFieldsValue({
        diagnostico_cie10: d.diagnostico_cie10 || d.diagnostico || '',
        aspectos_generales: d.aspectos_generales || d.aspecto_generales || '',
      });
      return;
    }
    // Crear: prefill desde última Evolución Psiquiátrica (EPM)
    if (vgi?.pacient && visible) {
      setLoading(true);
      reqwest({
        url: `${config.api_url}/patientprogress/prefill/?patient_id=${vgi.pacient}&type=EPM`,
        method: 'get',
        type: 'json',
        headers: { 'Authorization': `Token ${getToken()}` },
      }).then((resp) => {
        const pf = (resp && resp.data) ? resp.data : {};
        form.setFieldsValue({
          diagnostico_cie10: pf.diagnostico_cie10 || pf.diagnostico || pf.cie10 || '',
          aspectos_generales: '',
        });
      }).always(() => setLoading(false));
    }
  }, [record, vgi?.pacient, visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        diagnostico_cie10: values.diagnostico_cie10 || '',
        aspectos_generales: values.aspectos_generales || '',
      };
      await onSave(payload);
      onClose(true);
    } catch (e) {
      // validation error
    }
  };

  return (
    <Drawer
      width={640}
      title={record ? 'Valoración Psiquiátrica' : 'Nueva valoración Psiquiátrica'}
      onClose={() => onClose(false)}
      visible={visible}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={() => onClose(false)}>Cancelar</Button>
          <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Form.Item name="diagnostico_cie10" label="Diagnóstico CIE10">
            <Input disabled />
          </Form.Item>
          <Form.Item name="aspectos_generales" label="Aspectos generales">
            <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
}

function PsychologicalValuationDrawer({ visible, onClose, vgi, record, onSave, disabled }) {
  const [form] = Form.useForm();
  const [mmResult, setMmResult] = useState('');
  const [clockResult, setClockResult] = useState('');
  const [yesavageResult, setYesavageResult] = useState('');
  const [cdrResult, setCdrResult] = useState('');

  const interpretMinimental = (n) => {
    if (n === '' || n === null || n === undefined) return '';
    const v = Number(n);
    if (isNaN(v)) return '';
    if (v < 10) return 'Deterioro cognitivo severo';
    if (v >= 10 && v <= 19) return 'Deterioro cognitivo moderado';
    if (v >= 20 && v <= 25) return 'Deterioro cognitivo leve';
    if (v >= 25 && v <= 30) return 'Deterioro cognitivo dudoso';
    if (v > 30) return 'Sin deterioro cognitivo';
    return '';
  };

  const interpretClock = (n) => {
    if (n === '' || n === null || n === undefined) return '';
    const v = Number(n);
    if (isNaN(v)) return '';
    if (v < 6) return 'Positivo (deterioro cognitivo)';
    if (v > 6) return 'Negativo';
    // v === 6 no especificado: dejamos vacío
    return '';
  };

  const interpretYesavage = (n) => {
    if (n === '' || n === null || n === undefined) return '';
    const v = Number(n);
    if (isNaN(v)) return '';
    if (v >= 0 && v <= 5) return 'Tamizaje normal';
    if (v >= 6 && v <= 10) return 'Tamizaje depresión moderada';
    if (v >= 11 && v <= 15) return 'Tamizaje con depresión severa';
    return '';
  };

  const interpretCDR = (v) => {
    if (v === '' || v === null || v === undefined) return '';
    const m = String(v);
    if (m === '0') return 'Sano';
    if (m === '0.5') return 'Demencia cuestionable';
    if (m === '1') return 'Demencia leve';
    if (m === '2') return 'Demencia moderada';
    if (m === '3') return 'Demencia severa';
    return '';
  };

  useEffect(() => {
    form.resetFields();
    const d = (record && record.data) ? record.data : {};
    form.setFieldsValue({
      aspectos_generales: d.aspectos_generales || d.aspecto_generales || '',
      minimental: d.minimental ?? d.mmse ?? '',
      reloj: d.reloj ?? d.test_reloj ?? '',
      yesavage: d.yesavage ?? d.escala_yesavage ?? '',
      cdr: (d.cdr !== undefined && d.cdr !== null) ? String(d.cdr) : '',
    });
    setMmResult(interpretMinimental(d.minimental ?? d.mmse));
    setClockResult(interpretClock(d.reloj ?? d.test_reloj));
    setYesavageResult(interpretYesavage(d.yesavage ?? d.escala_yesavage));
    setCdrResult(interpretCDR((d.cdr !== undefined && d.cdr !== null) ? String(d.cdr) : ''));
  }, [record, visible]);

  const handleValuesChange = (changed, all) => {
    // Tomar siempre los valores actuales del formulario para evitar conservar interpretaciones al borrar
    const vals = form.getFieldsValue(['minimental', 'reloj', 'yesavage', 'cdr']);
    setMmResult(interpretMinimental(vals.minimental));
    setClockResult(interpretClock(vals.reloj));
    setYesavageResult(interpretYesavage(vals.yesavage));
    setCdrResult(interpretCDR(vals.cdr));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        aspectos_generales: values.aspectos_generales || '',
        minimental: values.minimental === '' ? '' : Number(values.minimental),
        minimental_interpretacion: interpretMinimental(values.minimental),
        test_reloj: values.reloj === '' ? '' : Number(values.reloj),
        test_reloj_interpretacion: interpretClock(values.reloj),
        yesavage: values.yesavage === '' ? '' : Number(values.yesavage),
        yesavage_interpretacion: interpretYesavage(values.yesavage),
        cdr: values.cdr === '' ? '' : values.cdr,
        cdr_interpretacion: interpretCDR(values.cdr),
      };
      await onSave(payload);
      onClose(true);
    } catch (e) {
      // validation handled by antd
    }
  };

  return (
    <Drawer
      width={680}
      title={record ? 'Valoración Psicológica' : 'Nueva valoración Psicológica'}
      onClose={() => onClose(false)}
      visible={visible}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={() => onClose(false)}>Cancelar</Button>
          <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <Form.Item name="aspectos_generales" label="Aspectos generales">
          <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="minimental" label="Minimental (0-34)">
              <Input type="number" min={0} max={34} placeholder="Ej: 28" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Interpretación">
              <Tag color={mmResult.includes('severo') ? 'red' : mmResult.includes('moderado') ? 'orange' : mmResult.includes('leve') ? 'gold' : mmResult.includes('dudoso') ? 'blue' : mmResult ? 'green' : ''}>{mmResult || '-'}</Tag>
            </Form.Item>
          </Col>
        </Row>
        <div style={{ fontSize: 12, color: '#888', marginTop: -12, marginBottom: 12 }}>
          Menos de 10: severo · 10-19: moderado · 20-25: leve · 25-30: dudoso · Mayor a 30: sin deterioro
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="reloj" label="Test del dibujo del reloj (1-10)">
              <Input type="number" min={1} max={10} placeholder="Ej: 7" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Interpretación">
              <Tag color={clockResult.startsWith('Positivo') ? 'red' : clockResult ? 'green' : ''}>{clockResult || '-'}</Tag>
            </Form.Item>
          </Col>
        </Row>
        <div style={{ fontSize: 12, color: '#888', marginTop: -12, marginBottom: 12 }}>
          Menor a 6: positivo (deterioro cognitivo) · Mayor a 6: negativo
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="yesavage" label="Escala de depresión geriátrica de Yesavage (0-15)">
              <Input type="number" min={0} max={15} placeholder="Ej: 4" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Interpretación">
              <Tag color={yesavageResult.includes('severa') ? 'red' : yesavageResult.includes('moderada') ? 'orange' : yesavageResult ? 'green' : ''}>{yesavageResult || '-'}
              </Tag>
            </Form.Item>
          </Col>
        </Row>
        <div style={{ fontSize: 12, color: '#888', marginTop: -12, marginBottom: 12 }}>
          0-5: normal · 6-10: depresión moderada · 11-15: depresión severa
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="cdr" label="CDR (0 / 0.5 / 1 / 2 / 3)">
              <Select allowClear disabled={disabled} placeholder="Seleccione">
                <Select.Option value="0">0</Select.Option>
                <Select.Option value="0.5">0.5</Select.Option>
                <Select.Option value="1">1</Select.Option>
                <Select.Option value="2">2</Select.Option>
                <Select.Option value="3">3</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Interpretación">
              <Input value={cdrResult || ''} readOnly />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
}

function SocialValuationDrawer({ visible, onClose, vgi, record, onSave, disabled }) {
  const [form] = Form.useForm();
  const [oarsText, setOarsText] = useState('');

  const interpretOARS = (v) => {
    if (v === '' || v === null || v === undefined) return '';
    const m = Number(v);
    if (isNaN(m)) return '';
    if (m === 1) return 'Excelentes recursos sociales';
    if (m === 2) return 'Buenos recursos sociales';
    if (m === 3) return 'Recursos sociales ligeramente deteriorados';
    if (m === 4) return 'Recursos sociales moderadamente deteriorados';
    if (m === 5) return 'Recursos sociales bastante deteriorados';
    if (m === 6) return 'Recursos sociales totalmente deteriorados';
    return '';
  };

  useEffect(() => {
    form.resetFields();
    const d = (record && record.data) ? record.data : {};
    form.setFieldsValue({
      aspectos_generales: d.aspectos_generales || d.aspecto_generales || '',
      red_apoyo: d.red_apoyo || d.red_de_apoyo || '',
      resena_social: d.resena_social || d.reseña_social || '',
      expectativas_residente: d.expectativas_residente || '',
      oars: (d.oars !== undefined && d.oars !== null && d.oars !== '') ? Number(d.oars) : undefined,
    });
    setOarsText(interpretOARS((d.oars !== undefined && d.oars !== null && d.oars !== '') ? Number(d.oars) : ''));
  }, [record, visible]);

  const handleValuesChange = () => {
    const vals = form.getFieldsValue(['oars']);
    setOarsText(interpretOARS(vals.oars));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        aspectos_generales: values.aspectos_generales || '',
        red_apoyo: values.red_apoyo || '',
        resena_social: values.resena_social || '',
        expectativas_residente: values.expectativas_residente || '',
        oars: (values.oars === undefined || values.oars === null || values.oars === '') ? '' : Number(values.oars),
        oars_interpretacion: interpretOARS(values.oars),
      };
      await onSave(payload);
      onClose(true);
    } catch (e) {
      // handled by antd
    }
  };

  return (
    <Drawer
      width={700}
      title={record ? 'Valoración Social' : 'Nueva valoración Social'}
      onClose={() => onClose(false)}
      visible={visible}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={() => onClose(false)}>Cancelar</Button>
          <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <Form.Item name="aspectos_generales" label="Aspectos generales">
          <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
        </Form.Item>
        <Form.Item name="red_apoyo" label="Red de apoyo">
          <TextArea autoSize={{ minRows: 3 }} disabled={disabled} />
        </Form.Item>
        <Form.Item name="resena_social" label="Reseña social">
          <TextArea autoSize={{ minRows: 3 }} disabled={disabled} />
        </Form.Item>
        <Form.Item name="expectativas_residente" label="Expectativas del residente">
          <TextArea autoSize={{ minRows: 3 }} disabled={disabled} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="oars" label="Escala de recursos sociales (OARS)">
              <Select allowClear placeholder="Seleccione" disabled={disabled}>
                <Select.Option value={1}>1</Select.Option>
                <Select.Option value={2}>2</Select.Option>
                <Select.Option value={3}>3</Select.Option>
                <Select.Option value={4}>4</Select.Option>
                <Select.Option value={5}>5</Select.Option>
                <Select.Option value={6}>6</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Interpretación">
              <Tag color={
                oarsText.includes('Excelentes') ? 'green' :
                oarsText.includes('Buenos') ? 'green' :
                oarsText.includes('ligeramente') ? 'gold' :
                oarsText.includes('moderadamente') ? 'orange' :
                oarsText.includes('bastante') ? 'red' :
                oarsText.includes('totalmente') ? 'red' : ''
              }>
                {oarsText || '-'}
              </Tag>
            </Form.Item>
          </Col>
        </Row>
        <div style={{ fontSize: 12, color: '#888', marginTop: -12 }}>
          1: Excelentes · 2: Buenos · 3: Ligeramente deteriorados · 4: Moderadamente deteriorados · 5: Bastante deteriorados · 6: Totalmente deteriorados
        </div>
      </Form>
    </Drawer>
  );
}

function ValoracionesList({ vgi, refresh, viewMode = 'cards', hospitalizationStatus, isReadOnly }) {
  const [items, setItems] = useState({});
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState({ visible: false, type: null, record: null });

  const isHospitalizationActive = hospitalizationStatus === 'A' && !isReadOnly;
  const canEdit = !vgi?.closed_at && isHospitalizationActive;

  const load = () => {
    if (!vgi) return;
    setLoading(true);
    // tipos
    reqwest({ url: `${config.api_url}/vgi-valoracion/types/`, method: 'get', type: 'json', headers: { 'Authorization': `Token ${getToken()}` } })
      .then((resp) => setTypes(Array.isArray(resp) ? resp : [])).catch(() => setTypes([]));
    // existentes
    reqwest({ url: `${config.api_url}/vgi-valoracion/?vgi=${vgi.id}`, method: 'get', type: 'json', headers: { 'Authorization': `Token ${getToken()}` } })
      .then((resp) => {
        const list = resp.results || resp || [];
        const map = {};
        list.forEach(r => { map[r.type] = r; });
        setItems(map);
      }).always(() => setLoading(false));
  };

  useEffect(() => { load(); }, [vgi?.id, refresh]);

  const handleSave = async (type, data, record) => {
    const url = record ? `${config.api_url}/vgi-valoracion/${record.id}/` : `${config.api_url}/vgi-valoracion/`;
    const method = record ? 'put' : 'post';
    await reqwest({ url, method, type: 'json', contentType: 'application/json', headers: { 'Authorization': `Token ${getToken()}` }, data: JSON.stringify({ vgi: vgi.id, type, data }) });
    message.success('Guardado');
    load();
  };

  const tableData = useMemo(() => types.map(t => {
    const rec = items[t.value];
    return {
      key: t.value,
      type: t.value,
      label: VALORACION_LABELS[t.value] || t.label || t.value,
      record: rec,
      created_at: rec?.created_at || null,
      updated_at: rec?.updated_at || null,
      created_by_username: rec?.created_by_username || null,
      updated_by_usernames: rec?.updated_by_usernames || [],
      completed_at: rec?.completed_at || null,
      completed_by_username: rec?.completed_by_username || null,
    };
  }), [types, items]);

  const columns = useMemo(() => ([
    { title: 'Tipo', dataIndex: 'label', key: 'label' },
    {
      title: 'Estado', key: 'estado', width: 120,
      render: (_, row) => row.record ? (row.record.completed_at ? <Tag color="blue">Completada</Tag> : <Tag color="green">Cargado</Tag>) : <Tag>Falta</Tag>
    },
    {
      title: 'Creada', dataIndex: 'created_at', key: 'created_at',
      render: (v) => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: 'Actualizada', dataIndex: 'updated_at', key: 'updated_at',
      render: (v) => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: 'Completada', dataIndex: 'completed_at', key: 'completed_at',
      render: (v) => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: 'Creado por', dataIndex: 'created_by_username', key: 'created_by_username',
      render: (v) => v || '-'
    },
    {
      title: 'Actualizado por', dataIndex: 'updated_by_usernames', key: 'updated_by_usernames',
      render: (arr) => (Array.isArray(arr) && arr.length) ? arr.join(', ') : '-'
    },
    {
      title: 'Completada por', dataIndex: 'completed_by_username', key: 'completed_by_username',
      render: (v) => v || '-'
    },
    {
      title: 'Acciones', key: 'acciones', width: 150,
      render: (_, row) => (
        <Space>
          {row.record && !row.record.completed_at && canEdit && (
            <Tooltip title="Marcar como completada">
              <Button type="link" onClick={async (e) => {
                e.stopPropagation();
                try {
                  await reqwest({ url: `${config.api_url}/vgi-valoracion/${row.record.id}/complete/`, method: 'post', headers: { 'Authorization': `Token ${getToken()}` } });
                  message.success('Valoración marcada como completada');
                  load();
                } catch (e) {
                  message.error('No se pudo completar la valoración');
                }
              }}>Completar</Button>
            </Tooltip>
          )}
          {!canEdit && <Tooltip title={!isHospitalizationActive ? "Internación no activa" : "VGI cerrada"}><ExclamationCircleOutlined /></Tooltip>}
        </Space>
      )
    }
  ]), [canEdit, isHospitalizationActive]);

  return (
    <Spin spinning={loading}>
      {viewMode === 'table' ? (
        <Table
          size="small"
          rowKey={(r) => r.key}
          dataSource={tableData}
          columns={columns}
          onRow={(row) => ({
            onClick: () => {
              // Permitir ver si existe registro; crear sólo si se puede editar
              if (row.record || canEdit) {
                setEditor({ visible: true, type: row.type, record: row.record || null });
              }
            }
          })}
          pagination={false}
        />
      ) : (
        <Row gutter={[12, 12]}>
          {types.map(t => {
            const val = items[t.value];
            const label = VALORACION_LABELS[t.value] || t.label || t.value;
            return (
              <Col xs={24} md={12} lg={8} key={t.value}>
                <Card
                  size="small"
                  hoverable
                  onClick={() => {
                    if (val || canEdit) {
                      setEditor({ visible: true, type: t.value, record: val || null });
                    }
                  }}
                  title={label}
                  extra={val ? (val.completed_at ? <Tag color="blue">Completada</Tag> : <Tag color="green">Cargado</Tag>) : <Tag>Falta</Tag>}
                >
                  <div style={{ fontSize: 12, marginBottom: 8 }}>
                    <div>Creado por: <b>{val?.created_by_username || '-'}</b></div>
                    <div>Actualizado por: <b>{(val?.updated_by_usernames && val.updated_by_usernames.length) ? val.updated_by_usernames.join(', ') : '-'}</b></div>
                    <div>Completada: <b>{val?.completed_at ? new Date(val.completed_at).toLocaleString() : '-'}</b></div>
                    <div>Completada por: <b>{val?.completed_by_username || '-'}</b></div>
                  </div>
                  <Space>
                    {val && !val.completed_at && canEdit && (
                      <Button onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await reqwest({ url: `${config.api_url}/vgi-valoracion/${val.id}/complete/`, method: 'post', headers: { 'Authorization': `Token ${getToken()}` } });
                          message.success('Valoración marcada como completada');
                          load();
                        } catch (e) {
                          message.error('No se pudo completar la valoración');
                        }
                      }}>Completar</Button>
                    )}
                    {!canEdit && <Tooltip title={!isHospitalizationActive ? "Internación no activa" : "VGI cerrada"}><ExclamationCircleOutlined /></Tooltip>}
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
      {editor.type === 'CLINICA' ? (
        <ClinicalValuationDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          vgi={vgi}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('CLINICA', data, editor.record);
          }}
        />
      ) : editor.type === 'ENFERMERIA' ? (
        <NursingValuationDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          vgi={vgi}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('ENFERMERIA', data, editor.record);
          }}
        />
      ) : editor.type === 'NUTRICIONAL' ? (
        <NutritionalValuationDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          vgi={vgi}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('NUTRICIONAL', data, editor.record);
          }}
        />
      ) : editor.type === 'PSIQUIATRICA' ? (
        <PsychiatricValuationDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          vgi={vgi}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('PSIQUIATRICA', data, editor.record);
          }}
        />
      ) : editor.type === 'FUNCIONAL' ? (
        <FunctionalValuationDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          vgi={vgi}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('FUNCIONAL', data, editor.record);
          }}
        />
      ) : editor.type === 'KINESICA' ? (
        <KinesicValuationDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          vgi={vgi}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('KINESICA', data, editor.record);
          }}
        />
      ) : editor.type === 'PSICOLOGICA' ? (
        <PsychologicalValuationDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          vgi={vgi}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('PSICOLOGICA', data, editor.record);
          }}
        />
      ) : editor.type === 'SOCIAL' ? (
        <SocialValuationDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          vgi={vgi}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('SOCIAL', data, editor.record);
          }}
        />
      ) : editor.type === 'PSIQUIATRICA' ? (
        <PsychiatricValuationDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          vgi={vgi}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('PSIQUIATRICA', data, editor.record);
          }}
        />
      ) : (
        <JsonEditorDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          title={editor.record ? `Editar valoración - ${VALORACION_LABELS[editor.type] || editor.type}` : `Nueva valoración - ${VALORACION_LABELS[editor.type] || editor.type}`}
          initialValue={editor.record?.data}
          onSave={(json) => handleSave(editor.type, json, editor.record)}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
        />
      )}
    </Spin>
  );
}

function PAIList({ vgi, refresh, viewMode = 'cards', hospitalizationStatus, isReadOnly }) {
  const [items, setItems] = useState({});
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState({ visible: false, type: null, record: null });
  const isHospitalizationActive = hospitalizationStatus === 'A' && !isReadOnly;
  const canEdit = !vgi?.closed_at && isHospitalizationActive;

  // Drawer simple para PAI con único campo "Propuesta"
  const SimplePAIDrawer = ({ visible, onClose, type, record, onSave, disabled }) => {
    const [form] = Form.useForm();

    useEffect(() => {
      form.resetFields();
      if (record && record.data) {
        form.setFieldsValue({ propuesta: record.data.propuesta || '' });
      } else {
        form.setFieldsValue({ propuesta: '' });
      }
    }, [record, visible]);

    const handleSubmit = async () => {
      try {
        const values = await form.validateFields();
        await onSave({ propuesta: values.propuesta || '' });
        onClose(true);
      } catch (e) {
        // handled by antd
      }
    };

    return (
      <Drawer
        width={640}
        title={record ? `PAI - ${PAI_LABELS[type] || type}` : `Nuevo PAI - ${PAI_LABELS[type] || type}`}
        onClose={() => onClose(false)}
        visible={visible}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => onClose(false)}>Cancelar</Button>
            <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="propuesta" label="Propuesta" rules={[{ required: true, message: 'Ingrese la propuesta' }]}>
            <TextArea autoSize={{ minRows: 6 }} disabled={disabled} />
          </Form.Item>
        </Form>
      </Drawer>
    );
  };

  // Drawer de PAI Clínico: Propuesta + Esquema médico (prefill)
  const ClinicalPAIDrawer = ({ visible, onClose, record, onSave, disabled }) => {
    const [form] = Form.useForm();
    const [pfLoading, setPfLoading] = useState(false);
    const [meds, setMeds] = useState([]);

    useEffect(() => {
      form.resetFields();
      // Cargar propuesta si existe
      if (record && record.data) {
        form.setFieldsValue({ propuesta: record.data.propuesta || '' });
        // Si ya tiene el esquema médico guardado, lo usamos.
        if (Array.isArray(record.data.esquema_medico)) {
          setMeds(record.data.esquema_medico);
          return;
        }
      } else {
        form.setFieldsValue({ propuesta: '' });
      }
      // Siempre traer el esquema actual para visualizar (activo o último) si no hay uno guardado
      if (vgi?.pacient && visible) {
        setPfLoading(true);
        reqwest({
          url: `${config.api_url}/patientprogress/prefill/?patient_id=${vgi.pacient}&type=ECM`,
          method: 'get',
          type: 'json',
          headers: { 'Authorization': `Token ${getToken()}` },
        }).then((resp) => {
          const rows = (resp && resp.data && Array.isArray(resp.data.tratamiento_farmacologico)) ? resp.data.tratamiento_farmacologico : [];
          setMeds(rows);
        }).always(() => setPfLoading(false));
      } else {
        setMeds([]);
      }
    }, [record, vgi?.pacient, visible]);

    const handleSubmit = async () => {
      try {
        const values = await form.validateFields();
        await onSave({
          propuesta: values.propuesta || '',
          esquema_medico: meds, // Persistimos el esquema mostrado
        });
        onClose(true);
      } catch (e) {
        // handled by antd
      }
    };

    const medColumns = [
      { title: 'Medicamento', dataIndex: 'medicamento', key: 'medicamento', width: 260, onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }) },
      { title: 'Presentación', dataIndex: 'presentacion', key: 'presentacion', width: 200, onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }) },
      { title: 'Dosis', dataIndex: 'dosis', key: 'dosis', width: 120, render: (val) => (val != null && val !== '') ? formatDoseByUnit(val, null) : '-' },
      { title: 'Momento', dataIndex: 'momento', key: 'momento', width: 160 },
    ];

    return (
      <Drawer
        width={760}
        title={record ? 'PAI - Clínica' : 'Nuevo PAI - Clínica'}
        onClose={() => onClose(false)}
        visible={visible}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => onClose(false)}>Cancelar</Button>
            <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="propuesta" label="Propuesta" rules={[{ required: true, message: 'Ingrese la propuesta' }] }>
            <TextArea autoSize={{ minRows: 6 }} disabled={disabled} />
          </Form.Item>
        </Form>
        <Divider orientation="left">Esquema médico (clínico) activo</Divider>
        <Spin spinning={pfLoading}>
          {(!meds || meds.length === 0) ? (
            <Empty description="No hay esquema clínico activo ni medicamentos asociados" />
          ) : (
            <Table
              size="small"
              rowKey={(r, idx) => `${r.medicamento}-${idx}`}
              dataSource={meds}
              columns={medColumns}
              pagination={false}
            />
          )}
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            Fuente: último esquema médico clínico activo de la internación vigente.
          </div>
        </Spin>
      </Drawer>
    );
  };

  // Drawer de PAI Psiquiátrico: Propuesta + Esquema médico (prefill desde EPM)
  const PsychiatricPAIDrawer = ({ visible, onClose, record, onSave, disabled }) => {
    const [form] = Form.useForm();
    const [pfLoading, setPfLoading] = useState(false);
    const [meds, setMeds] = useState([]);

    useEffect(() => {
      form.resetFields();
      // Cargar propuesta si existe
      if (record && record.data) {
        form.setFieldsValue({ propuesta: record.data.propuesta || '' });
        // Si ya tiene el esquema médico guardado, lo usamos.
        if (Array.isArray(record.data.esquema_medico)) {
          setMeds(record.data.esquema_medico);
          return;
        }
      } else {
        form.setFieldsValue({ propuesta: '' });
      }
      // Siempre traer el esquema actual para visualizar (activo o último) si no hay uno guardado
      if (vgi?.pacient && visible) {
        setPfLoading(true);
        reqwest({
          url: `${config.api_url}/patientprogress/prefill/?patient_id=${vgi.pacient}&type=EPM`,
          method: 'get',
          type: 'json',
          headers: { 'Authorization': `Token ${getToken()}` },
        }).then((resp) => {
          const data = resp && resp.data ? resp.data : {};
          // Aceptar ambas claves por compatibilidad
          const rows = Array.isArray(data.tratamiento_farmacologico)
            ? data.tratamiento_farmacologico
            : (Array.isArray(data.esquema_farmacologico) ? data.esquema_farmacologico : []);
          setMeds(rows);
        }).always(() => setPfLoading(false));
      } else {
        setMeds([]);
      }
    }, [record, vgi?.pacient, visible]);

    const handleSubmit = async () => {
      try {
        const values = await form.validateFields();
        await onSave({
          propuesta: values.propuesta || '',
          esquema_medico: meds, // Persistimos el esquema mostrado
        });
        onClose(true);
      } catch (e) {
        // handled by antd
      }
    };

    const medColumns = [
      { title: 'Medicamento', dataIndex: 'medicamento', key: 'medicamento', width: 260, onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }) },
      { title: 'Presentación', dataIndex: 'presentacion', key: 'presentacion', width: 200, onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }) },
      { title: 'Dosis', dataIndex: 'dosis', key: 'dosis', width: 120, render: (val) => (val != null && val !== '') ? formatDoseByUnit(val, null) : '-' },
      { title: 'Momento', dataIndex: 'momento', key: 'momento', width: 160 },
    ];

    return (
      <Drawer
        width={760}
        title={record ? 'PAI - Psiquiátrica' : 'Nuevo PAI - Psiquiátrica'}
        onClose={() => onClose(false)}
        visible={visible}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => onClose(false)}>Cancelar</Button>
            <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="propuesta" label="Propuesta" rules={[{ required: true, message: 'Ingrese la propuesta' }]}>
            <TextArea autoSize={{ minRows: 6 }} disabled={disabled} />
          </Form.Item>
        </Form>
        <Divider orientation="left">Esquema médico (psiquiátrico) activo</Divider>
        <Spin spinning={pfLoading}>
          {(!meds || meds.length === 0) ? (
            <Empty description="No hay esquema psiquiátrico activo ni medicamentos asociados" />
          ) : (
            <Table
              size="small"
              rowKey={(r, idx) => `${r.medicamento}-${idx}`}
              dataSource={meds}
              columns={medColumns}
              pagination={false}
            />
          )}
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            Fuente: último esquema médico psiquiátrico activo de la internación vigente.
          </div>
        </Spin>
      </Drawer>
    );
  };

  const load = () => {
    if (!vgi) return;
    setLoading(true);
    reqwest({ url: `${config.api_url}/vgi-pai/types/`, method: 'get', type: 'json', headers: { 'Authorization': `Token ${getToken()}` } })
      .then((resp) => setTypes(Array.isArray(resp) ? resp : [])).catch(() => setTypes([]));
    reqwest({ url: `${config.api_url}/vgi-pai/?vgi=${vgi.id}`, method: 'get', type: 'json', headers: { 'Authorization': `Token ${getToken()}` } })
      .then((resp) => {
        const list = resp.results || resp || [];
        const map = {};
        list.forEach(r => { map[r.type] = r; });
        setItems(map);
      }).always(() => setLoading(false));
  };

  useEffect(() => { load(); }, [vgi?.id, refresh]);

  const handleSave = async (type, data, record) => {
    const url = record ? `${config.api_url}/vgi-pai/${record.id}/` : `${config.api_url}/vgi-pai/`;
    const method = record ? 'put' : 'post';
    await reqwest({ url, method, type: 'json', contentType: 'application/json', headers: { 'Authorization': `Token ${getToken()}` }, data: JSON.stringify({ vgi: vgi.id, type, data }) });
    message.success('Guardado');
    load();
  };

  const tableData = useMemo(() => types.map(t => {
    const rec = items[t.value];
    return {
      key: t.value,
      type: t.value,
      label: PAI_LABELS[t.value] || t.label || t.value,
      record: rec,
      created_at: rec?.created_at || null,
      updated_at: rec?.updated_at || null,
      created_by_username: rec?.created_by_username || null,
      updated_by_usernames: rec?.updated_by_usernames || [],
      completed_at: rec?.completed_at || null,
      completed_by_username: rec?.completed_by_username || null,
    };
  }), [types, items]);

  const columns = useMemo(() => ([
    { title: 'Tipo', dataIndex: 'label', key: 'label' },
    {
      title: 'Estado', key: 'estado', width: 120,
      render: (_, row) => row.record ? (row.record.completed_at ? <Tag color="blue">Completada</Tag> : <Tag color="green">Cargado</Tag>) : <Tag>Falta</Tag>
    },
    {
      title: 'Creada', dataIndex: 'created_at', key: 'created_at',
      render: (v) => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: 'Actualizada', dataIndex: 'updated_at', key: 'updated_at',
      render: (v) => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: 'Completada', dataIndex: 'completed_at', key: 'completed_at',
      render: (v) => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: 'Creado por', dataIndex: 'created_by_username', key: 'created_by_username',
      render: (v) => v || '-'
    },
    {
      title: 'Actualizado por', dataIndex: 'updated_by_usernames', key: 'updated_by_usernames',
      render: (arr) => (Array.isArray(arr) && arr.length) ? arr.join(', ') : '-'
    },
    {
      title: 'Completada por', dataIndex: 'completed_by_username', key: 'completed_by_username',
      render: (v) => v || '-'
    },
    {
      title: 'Acciones', key: 'acciones', width: 150,
      render: (_, row) => (
        <Space>
          {row.record && !row.record.completed_at && canEdit && (
            <Tooltip title="Marcar como completada">
              <Button type="link" onClick={async (e) => {
                e.stopPropagation();
                try {
                  await reqwest({ url: `${config.api_url}/vgi-pai/${row.record.id}/complete/`, method: 'post', headers: { 'Authorization': `Token ${getToken()}` } });
                  message.success('PAI marcado como completado');
                  load();
                } catch (e) {
                  message.error('No se pudo completar el PAI');
                }
              }}>Completar</Button>
            </Tooltip>
          )}
          {!canEdit && <Tooltip title={!isHospitalizationActive ? "Internación no activa" : "VGI cerrada"}><ExclamationCircleOutlined /></Tooltip>}
        </Space>
      )
    }
  ]), [canEdit, isHospitalizationActive]);

  return (
    <Spin spinning={loading}>
      {viewMode === 'table' ? (
        <Table
          size="small"
          rowKey={(r) => r.key}
          dataSource={tableData}
          columns={columns}
          onRow={(row) => ({
            onClick: () => {
              // Permitir ver si existe registro; crear sólo si se puede editar
              if (row.record || canEdit) {
                setEditor({ visible: true, type: row.type, record: row.record || null });
              }
            }
          })}
          pagination={false}
        />
      ) : (
        <Row gutter={[12, 12]}>
          {types.map(t => {
            const val = items[t.value];
            const label = PAI_LABELS[t.value] || t.label || t.value;
            return (
              <Col xs={24} md={12} lg={8} key={t.value}>
                <Card
                  size="small"
                  hoverable
                  onClick={() => {
                    if (val || canEdit) {
                      setEditor({ visible: true, type: t.value, record: val || null });
                    }
                  }}
                  title={label}
                  extra={val ? (val.completed_at ? <Tag color="blue">Completada</Tag> : <Tag color="green">Cargado</Tag>) : <Tag>Falta</Tag>}
                >
                  <div style={{ fontSize: 12, marginBottom: 8 }}>
                    <div>Creado por: <b>{val?.created_by_username || '-'}</b></div>
                    <div>Actualizado por: <b>{(val?.updated_by_usernames && val.updated_by_usernames.length) ? val.updated_by_usernames.join(', ') : '-'}</b></div>
                    <div>Completada: <b>{val?.completed_at ? new Date(val.completed_at).toLocaleString() : '-'}</b></div>
                    <div>Completada por: <b>{val?.completed_by_username || '-'}</b></div>
                  </div>
                  <Space>
                    {val && !val.completed_at && canEdit && (
                      <Button onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await reqwest({ url: `${config.api_url}/vgi-pai/${val.id}/complete/`, method: 'post', headers: { 'Authorization': `Token ${getToken()}` } });
                          message.success('PAI marcado como completado');
                          load();
                        } catch (e) {
                          message.error('No se pudo completar el PAI');
                        }
                      }}>Completar</Button>
                    )}
                    {!canEdit && <Tooltip title={!isHospitalizationActive ? "Internación no activa" : "VGI cerrada"}><ExclamationCircleOutlined /></Tooltip>}
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
      {editor.type === 'CLINICA' ? (
        <ClinicalPAIDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('CLINICA', data, editor.record);
          }}
        />
      ) : editor.type === 'PSIQUIATRICA' ? (
        <PsychiatricPAIDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave('PSIQUIATRICA', data, editor.record);
          }}
        />
      ) : ['OBJETIVO_GENERAL','ENFERMERIA','NUTRICIONAL','OCUPACIONAL_RECREACION','KINESICA','PSICOLOGICA','SOCIAL'].includes(editor.type) ? (
        <SimplePAIDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          type={editor.type}
          record={editor.record}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
          onSave={async (data) => {
            await handleSave(editor.type, data, editor.record);
          }}
        />
      ) : (
        <JsonEditorDrawer
          visible={editor.visible}
          onClose={() => setEditor({ visible: false, type: null, record: null })}
          title={editor.record ? `Editar PAI - ${PAI_LABELS[editor.type] || editor.type}` : `Nuevo PAI - ${PAI_LABELS[editor.type] || editor.type}`}
          initialValue={editor.record?.data}
          onSave={(json) => handleSave(editor.type, json, editor.record)}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
        />
      )}
    </Spin>
  );
}

function PAIUpdateList({ vgi, refresh, viewMode = 'cards', hospitalizationStatus, isReadOnly }) {
  const [items, setItems] = useState({});
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState({ visible: false, type: null, record: null });
  const isHospitalizationActive = hospitalizationStatus === 'A' && !isReadOnly;
  const canEdit = !vgi?.closed_at && isHospitalizationActive;

  const SemestralDrawer = ({ visible, onClose, type, record, onSave, disabled }) => {
    const [form] = Form.useForm();
    const [pfLoading, setPfLoading] = useState(false);

    const loadPrefill = () => {
      if (!vgi?.id || !type) return;
      setPfLoading(true);
      reqwest({
        url: `${config.api_url}/vgi-pai-semestral/prefill/?vgi=${vgi.id}&type=${type}`,
        method: 'get',
        type: 'json',
        headers: { 'Authorization': `Token ${getToken()}` },
      }).then((resp) => {
        const prev = resp && resp.data && resp.data.propuesta_intervencion_previa;
        const current = form.getFieldValue('propuesta_intervencion_previa');
        if (!current && typeof prev === 'string') {
          form.setFieldsValue({ propuesta_intervencion_previa: prev });
        }
      }).always(() => setPfLoading(false));
    };

    useEffect(() => {
      form.resetFields();
      if (record && record.data) {
        form.setFieldsValue({
          actualizacion: record.data.actualizacion || '',
          propuesta_intervencion_previa: record.data.propuesta_intervencion_previa || '',
          avances_reajustes_comentarios: record.data.avances_reajustes_comentarios || '',
        });
      } else {
        form.setFieldsValue({ actualizacion: '', propuesta_intervencion_previa: '', avances_reajustes_comentarios: '' });
        // Cargar propuesta previa desde backend
        loadPrefill();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record, visible, type, vgi?.id]);

    const handleSubmit = async () => {
      try {
        const values = await form.validateFields();
        await onSave({
          actualizacion: values.actualizacion || '',
          propuesta_intervencion_previa: values.propuesta_intervencion_previa || '',
          avances_reajustes_comentarios: values.avances_reajustes_comentarios || '',
        });
        onClose(true);
      } catch (e) {
        // handled by antd
      }
    };

    return (
      <Drawer
        width={700}
        title={record ? `PAI (actualización semestral) - ${PAI_LABELS[type] || type}` : `Nuevo PAI (actualización semestral) - ${PAI_LABELS[type] || type}`}
        onClose={() => onClose(false)}
        visible={visible}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => onClose(false)}>Cancelar</Button>
            <Button type="primary" onClick={handleSubmit} disabled={disabled}>Guardar</Button>
          </Space>
        }
      >
        <Spin spinning={pfLoading}>
          <Form form={form} layout="vertical">
            <Form.Item name="actualizacion" label="Actualización" rules={[{ required: true, message: 'Ingrese la actualización' }]}>
              <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
            </Form.Item>
            <Form.Item name="propuesta_intervencion_previa" label="Propuesta de intervención previa">
              <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
            </Form.Item>
            <Form.Item name="avances_reajustes_comentarios" label="Avances / Reajustes de plan terapéutico / comentarios">
              <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>
    );
  };

  // Drawer específico para PAI (act. semestral) CLÍNICA: mismos campos y comportamiento que la VALORACIÓN clínica
  const SemestralClinicalPAIDrawer = ({ visible, onClose, record, onSave, disabled }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [medsLoading, setMedsLoading] = useState(false);
    const [meds, setMeds] = useState([]);

    // Reusar lógica de ClinicalValuationDrawer: prefill desde ECM y mapeo de Examen Físico
    const formatExamValue = (val, kind) => {
      if (val == null) return '';
      if (typeof val === 'string') return val;
      if (typeof val !== 'object') return String(val || '');
      const v = val || {};
      const pick = (k) => (v[k] == null ? '' : (typeof v[k] === 'object' ? '' : String(v[k])));
      if (kind === 'ppr' || kind === 'puntos_ureterales') {
        const r = pick('resultado');
        const loc = pick('localizacion');
        return [r, loc].filter(Boolean).join('/');
      }
      if (kind === 'sistema_digestivo' || kind === 'sistema_genitourinario' || kind === 'aparato_respiratorio' || kind === 'auscultacion_cardiaca') {
        const r = pick('resultado');
        const det = pick('detalle');
        return [r, det].filter(Boolean).join(' - ');
      }
      const keys = ['resultado', 'detalle', 'localizacion', 'value'];
      const parts = keys.map(pick).filter(Boolean);
      if (parts.length) return parts.join(' ');
      try { return JSON.stringify(val); } catch (e) { return String(val); }
    };

    const setEFFields = (ef = {}) => {
      form.setFieldsValue({
        ef_ta: ef.ta || ef.TA || '',
        ef_fc: ef.fc || ef.F_C || ef.Fc || ef.F_Card || '',
        ef_temperatura: ef.temperatura || ef.temp || '',
        ef_hgt: ef.hgt || ef.HGT || '',
        ef_sat: ef.sat || ef.SAT || ef.saturacion || ef.saturación || '',
        ef_sistema_digestivo: formatExamValue(ef.sistema_digestivo || ef.digestivo || '', 'sistema_digestivo'),
        ef_sistema_genitourinario: formatExamValue(ef.sistema_genitourinario || ef.genitourinario || '', 'sistema_genitourinario'),
        ef_puntos_uretrales: formatExamValue(ef.puntos_uretrales || ef.p_uretrales || '', 'puntos_ureterales'),
        ef_ppr: formatExamValue(ef.ppr || '', 'ppr'),
        ef_aparato_respiratorio: formatExamValue(ef.aparato_respiratorio || ef.respiratorio || '', 'aparato_respiratorio'),
        ef_auscultacion_cardiaca: formatExamValue(ef.auscultacion_cardiaca || ef.auscultación_cardiaca || ef.cardiaca || '', 'auscultacion_cardiaca'),
      });
    };

    useEffect(() => {
      form.resetFields();
      if (record && record.data) {
        // Cargar valores existentes del semestral clínico
        form.setFieldsValue({
          descripcion: record.data.descripcion || '',
          diagnosticos: record.data.diagnosticos || '',
        });
        if (record.data.examen_fisico && typeof record.data.examen_fisico === 'object') {
          setEFFields(record.data.examen_fisico);
        }
        // Si ya tiene el esquema médico guardado, lo usamos.
        if (Array.isArray(record.data.esquema_medico)) {
          setMeds(record.data.esquema_medico);
          return;
        }
        // Cargar tratamiento farmacológico (esquema clínico) vía patientprogress/prefill ECM si no hay uno guardado
        if (visible && vgi?.pacient) {
          setMedsLoading(true);
          reqwest({
            url: `${config.api_url}/patientprogress/prefill/?patient_id=${vgi.pacient}&type=ECM`,
            method: 'get',
            type: 'json',
            headers: { 'Authorization': `Token ${getToken()}` },
          })
            .then((resp) => {
              const rows = (resp && resp.data && Array.isArray(resp.data.tratamiento_farmacologico)) ? resp.data.tratamiento_farmacologico : [];
              setMeds(rows);
            })
            .always(() => setMedsLoading(false));
        }
        return;
      }
      // Prefill al crear: desde último ECM del paciente
      if (vgi?.pacient && visible) {
        setLoading(true);
        reqwest({
          url: `${config.api_url}/patientprogress/prefill/?patient_id=${vgi.pacient}&type=ECM`,
          method: 'get',
          type: 'json',
          headers: { 'Authorization': `Token ${getToken()}` },
        })
          .then((resp) => {
            const pf = resp && resp.data ? resp.data : {};
            const efRaw = pf.examen_fisico || pf.physical_exam || pf.ef || null;
            const diagnosticos = pf.diagnostico || pf.diagnosticos || pf.diagnosis || '';
            form.setFieldsValue({
              descripcion: '',
              diagnosticos: diagnosticos,
            });
            // Cargar tratamiento farmacológico desde ECM
            const rows = Array.isArray(pf.tratamiento_farmacologico) ? pf.tratamiento_farmacologico : [];
            setMeds(rows);
            const extraEF = {
              sistema_digestivo: formatExamValue(pf.sistema_digestivo, 'sistema_digestivo') || undefined,
              sistema_genitourinario: formatExamValue(pf.sistema_genitourinario, 'sistema_genitourinario') || undefined,
              puntos_uretrales: formatExamValue(pf.puntos_ureterales || pf.puntos_uretrales, 'puntos_ureterales') || undefined,
              ppr: (efRaw && typeof efRaw === 'object' && (efRaw.ppr || efRaw.PPR))
                ? formatExamValue(efRaw.ppr || efRaw.PPR, 'ppr')
                : formatExamValue(pf.ppr || pf.PPR, 'ppr') || undefined,
              aparato_respiratorio: formatExamValue(pf.aparato_respiratorio, 'aparato_respiratorio') || undefined,
              auscultacion_cardiaca: formatExamValue(pf.auscultacion_cardiaca || pf.auscultación_cardiaca, 'auscultacion_cardiaca') || undefined,
            };
            if (efRaw && typeof efRaw === 'object') {
              setEFFields({ ...efRaw, ...extraEF });
            } else if (typeof efRaw === 'string') {
              const text = efRaw;
              const pick = (labelPatterns) => {
                const patterns = Array.isArray(labelPatterns) ? labelPatterns : [labelPatterns];
                for (const p of patterns) {
                  const re = new RegExp(`${p}\s*[:.-]?\s*([^\n;]+)`, 'i');
                  const m = text.match(re);
                  if (m) return m[1].trim();
                }
                return '';
              };
              setEFFields({
                ta: pick(['TA', 'Tensión arterial', 'Tension arterial']),
                fc: pick(['F.C', 'FC', 'Frecuencia cardiaca', 'Frecuencia cardíaca']),
                temperatura: pick(['Temperatura', 'Temp']),
                hgt: pick(['HGT', 'Glucemia'] ),
                sat: pick(['SAT', 'Saturación', 'Saturacion']),
                sistema_digestivo: extraEF.sistema_digestivo !== undefined ? extraEF.sistema_digestivo : pick(['Sistema Digestivo', 'Digestivo']),
                sistema_genitourinario: extraEF.sistema_genitourinario !== undefined ? extraEF.sistema_genitourinario : pick(['Sistema Genitourinario', 'Genitourinario']),
                puntos_uretrales: extraEF.puntos_uretrales !== undefined ? extraEF.puntos_uretrales : pick(['Puntos Uretrales']),
                ppr: extraEF.ppr !== undefined ? extraEF.ppr : pick(['PPR']),
                aparato_respiratorio: extraEF.aparato_respiratorio !== undefined ? extraEF.aparato_respiratorio : pick(['Aparato Respiratorio', 'Respiratorio']),
                auscultacion_cardiaca: extraEF.auscultacion_cardiaca !== undefined ? extraEF.auscultacion_cardiaca : pick(['Auscultación Cardíaca', 'Auscultacion Cardiaca', 'Cardiaca']),
              });
            }
          })
          .always(() => setLoading(false));
        // Ya se carga tratamiento farmacológico en la misma llamada de ECM arriba
      }
    }, [record, vgi?.pacient, visible]);

    const handleSubmit = async () => {
      try {
        const values = await form.validateFields();
        const examen_fisico = {
          ta: values.ef_ta || '',
          fc: values.ef_fc || '',
          temperatura: values.ef_temperatura || '',
          hgt: values.ef_hgt || '',
          sat: values.ef_sat || '',
          sistema_digestivo: values.ef_sistema_digestivo || '',
          sistema_genitourinario: values.ef_sistema_genitourinario || '',
          puntos_uretrales: values.ef_puntos_uretrales || '',
          ppr: values.ef_ppr || '',
          aparato_respiratorio: values.ef_aparato_respiratorio || '',
          auscultacion_cardiaca: values.ef_auscultacion_cardiaca || '',
        };
        const data = {
          descripcion: values.descripcion || '',
          examen_fisico,
          diagnosticos: values.diagnosticos || '',
          esquema_medico: meds,
        };
        await onSave(data);
        onClose(true);
      } catch (e) {
        // handled by antd
      }
    };

    const medColumns = [
      { title: 'Medicamento', dataIndex: 'medicamento', key: 'medicamento', width: 260, onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }) },
      { title: 'Presentación', dataIndex: 'presentacion', key: 'presentacion', width: 200, onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }) },
      { title: 'Dosis', dataIndex: 'dosis', key: 'dosis', width: 120, render: (val) => (val != null && val !== '') ? formatDoseByUnit(val, null) : '-' },
      { title: 'Momento', dataIndex: 'momento', key: 'momento', width: 160 },
    ];

    return (
      <Drawer
        width={700}
        title={record ? 'PAI (act. semestral) - Clínica' : 'Nuevo PAI (act. semestral) - Clínica'}
        visible={visible}
        onClose={() => onClose(false)}
        destroyOnClose
        extra={(
          <Space>
            <Button onClick={() => onClose(false)}>Cancelar</Button>
            <Button type="primary" onClick={handleSubmit} loading={loading} disabled={disabled}>Guardar</Button>
          </Space>
        )}
      >
        <Spin spinning={loading}>
          <Form form={form} layout="vertical">
            <Form.Item name="descripcion" label="Descripción" rules={[{ required: true, message: 'Ingrese una descripción' }]}>
              <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
            </Form.Item>

            <Divider orientation="left">Examen Físico</Divider>
            <Row gutter={12}>
              <Col span={8}><Form.Item name="ef_ta" label="TA"><Input disabled={disabled} /></Form.Item></Col>
              <Col span={8}><Form.Item name="ef_fc" label="FC"><Input disabled={disabled} /></Form.Item></Col>
              <Col span={8}><Form.Item name="ef_temperatura" label="Temperatura"><Input disabled={disabled} /></Form.Item></Col>
            </Row>
            <Row gutter={12}>
              <Col span={8}><Form.Item name="ef_hgt" label="HGT"><Input disabled={disabled} /></Form.Item></Col>
              <Col span={8}><Form.Item name="ef_sat" label="SAT"><Input disabled={disabled} /></Form.Item></Col>
              <Col span={8}><Form.Item name="ef_puntos_uretrales" label="Puntos Uretrales"><Input disabled={disabled} /></Form.Item></Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}><Form.Item name="ef_sistema_digestivo" label="Sistema Digestivo"><Input disabled={disabled} /></Form.Item></Col>
              <Col span={12}><Form.Item name="ef_sistema_genitourinario" label="Sistema Genitourinario"><Input disabled={disabled} /></Form.Item></Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}><Form.Item name="ef_aparato_respiratorio" label="Aparato Respiratorio"><Input disabled={disabled} /></Form.Item></Col>
              <Col span={12}><Form.Item name="ef_auscultacion_cardiaca" label="Auscultación Cardíaca"><Input disabled={disabled} /></Form.Item></Col>
            </Row>

            <Form.Item name="diagnosticos" label="Diagnósticos">
              <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
            </Form.Item>
          </Form>
          <Divider orientation="left">Tratamiento farmacológico (ECM)</Divider>
          <Spin spinning={medsLoading}>
            {(!meds || meds.length === 0) ? (
              <Empty description="No hay esquema clínico activo ni medicamentos asociados" />
            ) : (
              <Table
                size="small"
                rowKey={(r, idx) => `${r.medicamento}-${idx}`}
                dataSource={meds}
                columns={medColumns}
                pagination={false}
              />
            )}
            <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
              Fuente: ECM (esquema clínico activo de la internación vigente).
            </div>
          </Spin>
        </Spin>
      </Drawer>
    );
  };

  // Drawer específico para PAI (act. semestral) PSIQUIÁTRICA: mantener campos y agregar esquema psiquiátrico vía EPM
  const SemestralPsychiatricPAIDrawer = ({ visible, onClose, record, onSave, disabled }) => {
    const [form] = Form.useForm();
    const [pfLoading, setPfLoading] = useState(false);
    const [medsLoading, setMedsLoading] = useState(false);
    const [meds, setMeds] = useState([]);

    const loadPrevPropuesta = () => {
      // reutilizamos el prefill general semestral solo para propuesta_intervencion_previa
      if (!vgi?.id) return;
      setPfLoading(true);
      reqwest({
        url: `${config.api_url}/vgi-pai-semestral/prefill/?vgi=${vgi.id}&type=PSIQUIATRICA`,
        method: 'get',
        type: 'json',
        headers: { 'Authorization': `Token ${getToken()}` },
      })
        .then((resp) => {
          const prev = resp && resp.data && resp.data.propuesta_intervencion_previa;
          const current = form.getFieldValue('propuesta_intervencion_previa');
          if (!current && typeof prev === 'string') {
            form.setFieldsValue({ propuesta_intervencion_previa: prev });
          }
        })
        .always(() => setPfLoading(false));
    };

    const loadPsychMeds = () => {
      if (!vgi?.pacient || !visible) return;
      setMedsLoading(true);
      reqwest({
        url: `${config.api_url}/patientprogress/prefill/?patient_id=${vgi.pacient}&type=EPM`,
        method: 'get',
        type: 'json',
        headers: { 'Authorization': `Token ${getToken()}` },
      })
        .then((resp) => {
          const rows = (resp && resp.data && Array.isArray(resp.data.tratamiento_farmacologico)) ? resp.data.tratamiento_farmacologico : [];
          setMeds(rows);
          // Prefill de diagnóstico si viene desde EPM
          try {
            const dx = resp && resp.data ? resp.data.diagnostico : '';
            const currentDx = form.getFieldValue('diagnostico');
            if (!currentDx && typeof dx === 'string') {
              form.setFieldsValue({ diagnostico: dx });
            }
          } catch (e) {}
        })
        .always(() => setMedsLoading(false));
    };

    useEffect(() => {
      form.resetFields();
      if (record && record.data) {
        form.setFieldsValue({
          actualizacion: record.data.actualizacion || '',
          propuesta_intervencion_previa: record.data.propuesta_intervencion_previa || '',
          avances_reajustes_comentarios: record.data.avances_reajustes_comentarios || '',
          diagnostico: record.data.diagnostico || '',
        });
        // Si ya tiene el esquema médico guardado, lo usamos.
        if (Array.isArray(record.data.esquema_medico)) {
          setMeds(record.data.esquema_medico);
        } else {
          // Cargar esquema psiquiátrico desde EPM al editar para visualizar si no hay uno guardado
          loadPsychMeds();
        }
        return;
      }
      // Nuevo registro: limpiar y cargar propuesta previa + esquema psiquiátrico
      form.setFieldsValue({ actualizacion: '', propuesta_intervencion_previa: '', avances_reajustes_comentarios: '', diagnostico: '' });
      loadPrevPropuesta();
      loadPsychMeds();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record, visible, vgi?.id, vgi?.pacient]);

    const handleSubmit = async () => {
      try {
        const values = await form.validateFields();
        await onSave({
          actualizacion: values.actualizacion || '',
          propuesta_intervencion_previa: values.propuesta_intervencion_previa || '',
          avances_reajustes_comentarios: values.avances_reajustes_comentarios || '',
          diagnostico: values.diagnostico || '',
          esquema_medico: meds,
        });
        onClose(true);
      } catch (e) {
        // handled by antd
      }
    };

    const medColumns = [
      { title: 'Medicamento', dataIndex: 'medicamento', key: 'medicamento', width: 260, onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }) },
      { title: 'Presentación', dataIndex: 'presentacion', key: 'presentacion', width: 200, onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }) },
      { title: 'Dosis', dataIndex: 'dosis', key: 'dosis', width: 120, render: (val) => (val != null && val !== '') ? formatDoseByUnit(val, null) : '-' },
      { title: 'Momento', dataIndex: 'momento', key: 'momento', width: 160 },
    ];

    return (
      <Drawer
        width={700}
        title={record ? 'PAI (act. semestral) - Psiquiátrica' : 'Nuevo PAI (act. semestral) - Psiquiátrica'}
        visible={visible}
        onClose={() => onClose(false)}
        destroyOnClose
        extra={(
          <Space>
            <Button onClick={() => onClose(false)}>Cancelar</Button>
            <Button type="primary" onClick={handleSubmit} loading={pfLoading} disabled={disabled}>Guardar</Button>
          </Space>
        )}
      >
        <Spin spinning={pfLoading}>
          <Form form={form} layout="vertical">
            <Form.Item name="actualizacion" label="Actualización" rules={[{ required: true, message: 'Ingrese la actualización' }]}> 
              <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
            </Form.Item>
            <Form.Item name="diagnostico" label="Diagnóstico">
              <TextArea autoSize={{ minRows: 3 }} disabled={disabled} />
            </Form.Item>
            <Form.Item name="propuesta_intervencion_previa" label="Propuesta de intervención previa">
              <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
            </Form.Item>
            <Form.Item name="avances_reajustes_comentarios" label="Avances / Reajustes de plan terapéutico / comentarios">
              <TextArea autoSize={{ minRows: 4 }} disabled={disabled} />
            </Form.Item>
          </Form>
        </Spin>
        <Divider orientation="left">Tratamiento farmacológico (EPM)</Divider>
        <Spin spinning={medsLoading}>
          {(!meds || meds.length === 0) ? (
            <Empty description="No hay esquema psiquiátrico activo ni medicamentos asociados" />
          ) : (
            <Table
              size="small"
              rowKey={(r, idx) => `${r.medicamento}-${idx}`}
              dataSource={meds}
              columns={medColumns}
              pagination={false}
            />
          )}
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            Fuente: EPM (esquema psiquiátrico activo de la internación vigente).
          </div>
        </Spin>
      </Drawer>
    );
  };

  const load = () => {
    if (!vgi?.id) return;
    setLoading(true);
    // tipos
    reqwest({ url: `${config.api_url}/vgi-pai-semestral/types/`, method: 'get', type: 'json', headers: { 'Authorization': `Token ${getToken()}` } })
      .then((tt) => {
        setTypes(Array.isArray(tt) ? tt : []);
      })
      .always(() => {});
    // items
    reqwest({ url: `${config.api_url}/vgi-pai-semestral/?vgi=${vgi.id}`, method: 'get', type: 'json', headers: { 'Authorization': `Token ${getToken()}` } })
      .then((resp) => {
        // Soportar tanto respuesta plana (array) como paginada { results: [...] }
        const rows = Array.isArray(resp)
          ? resp
          : (resp && Array.isArray(resp.results) ? resp.results : []);
        const grouped = {};
        rows.forEach((it) => { grouped[it.type] = it; });
        setItems(grouped);
      })
      .always(() => setLoading(false));
  };

  useEffect(() => { load(); }, [vgi?.id]);

  const handleSave = async (type, data, record) => {
    const payload = { vgi: vgi.id, type, data };
    const url = record ? `${config.api_url}/vgi-pai-semestral/${record.id}/` : `${config.api_url}/vgi-pai-semestral/`;
    const method = record ? 'put' : 'post';
    try {
      await reqwest({ url, method, data: JSON.stringify(payload), contentType: 'application/json', headers: { 'Authorization': `Token ${getToken()}` } });
      message.success('Guardado');
      load();
      refresh && refresh();
    } catch (e) {
      message.error('No se pudo guardar');
    }
  };

  const handleComplete = async (record) => {
    try {
      await reqwest({ url: `${config.api_url}/vgi-pai-semestral/${record.id}/complete/`, method: 'post', headers: { 'Authorization': `Token ${getToken()}` } });
      message.success('PAI (act. semestral) marcado como completado');
      load();
      refresh && refresh();
    } catch (e) {
      message.error('No se pudo completar el PAI (act. semestral)');
    }
  };

  const renderCard = (t) => {
    const rec = items[t.value];
    const completed = !!rec?.completed_at;
    return (
      <Card
        key={t.value}
        size="small"
        hoverable
        onClick={() => {
          // Abrir editor al hacer click en toda la tarjeta (como en los otros módulos)
          if (rec || canEdit) {
            setEditor({ visible: true, type: t.value, record: rec || null });
          }
        }}
        title={PAI_LABELS[t.value] || t.label || t.value}
        extra={completed ? <Tag color="blue">Completado</Tag> : <Tag color="green">En edición</Tag>}
        actions={[
          rec && !completed ? (
            <Button
              type="link"
              onClick={(e) => { e.stopPropagation(); handleComplete(rec); }}
              disabled={!canEdit}
            >
              Completar
            </Button>
          ) : <span />,
        ]}
      >
        <div style={{ minHeight: 60 }}>
          {rec && rec.data ? (
            t.value === 'CLINICA' ? (
              <>
                <p><b>Descripción:</b> {rec.data.descripcion || '-'}</p>
                <p><b>Diagnósticos:</b> {rec.data.diagnosticos || '-'}</p>
              </>
            ) : (
              <>
                <p><b>Actualización:</b> {rec.data.actualizacion || '-'}</p>
                <p><b>Propuesta previa:</b> {rec.data.propuesta_intervencion_previa || '-'}</p>
                <p><b>Avances/Reajustes/Comentarios:</b> {rec.data.avances_reajustes_comentarios || '-'}</p>
              </>
            )
          ) : <em>Sin datos</em>}
        </div>
      </Card>
    );
  };

  // Datos para vista tabla (igual a PAI normal/Valoraciones)
  const tableData = useMemo(() => types.map(t => {
    const rec = items[t.value];
    return {
      key: t.value,
      type: t.value,
      label: PAI_LABELS[t.value] || t.label || t.value,
      record: rec,
      created_at: rec?.created_at || null,
      updated_at: rec?.updated_at || null,
      created_by_username: rec?.created_by_username || null,
      updated_by_usernames: rec?.updated_by_usernames || [],
      completed_at: rec?.completed_at || null,
      completed_by_username: rec?.completed_by_username || null,
    };
  }), [types, items]);

  const columns = useMemo(() => ([
    { title: 'Tipo', dataIndex: 'label', key: 'label' },
    {
      title: 'Estado', key: 'estado', width: 120,
      render: (_, row) => row.record ? (row.record.completed_at ? <Tag color="blue">Completado</Tag> : <Tag color="green">Cargado</Tag>) : <Tag>Falta</Tag>
    },
    { title: 'Creado', dataIndex: 'created_at', key: 'created_at', render: (v) => v ? new Date(v).toLocaleString() : '-' },
    { title: 'Actualizado', dataIndex: 'updated_at', key: 'updated_at', render: (v) => v ? new Date(v).toLocaleString() : '-' },
    { title: 'Completado', dataIndex: 'completed_at', key: 'completed_at', render: (v) => v ? new Date(v).toLocaleString() : '-' },
    { title: 'Creado por', dataIndex: 'created_by_username', key: 'created_by_username', render: (v) => v || '-' },
    { title: 'Actualizado por', dataIndex: 'updated_by_usernames', key: 'updated_by_usernames', render: (arr) => (Array.isArray(arr) && arr.length) ? arr.join(', ') : '-' },
    { title: 'Completado por', dataIndex: 'completed_by_username', key: 'completed_by_username', render: (v) => v || '-' },
    {
      title: 'Acciones', key: 'acciones', width: 170,
      render: (_, row) => (
        <Space>
          {row.record && !row.record.completed_at && canEdit && (
            <Tooltip title="Marcar como completado">
              <Button type="link" onClick={async (e) => { e.stopPropagation(); await handleComplete(row.record); }}>Completar</Button>
            </Tooltip>
          )}
        </Space>
      )
    },
  ]), [canEdit, handleComplete]);

  return (
    <Spin spinning={loading}>
      {viewMode === 'cards' ? (
        <Row gutter={[12, 12]}>
          {types.map((t) => (
            <Col xs={24} md={12} lg={8} key={t.value}>
              {renderCard(t)}
            </Col>
          ))}
        </Row>
      ) : (
        <Table
          size="small"
          rowKey={(row) => row.key}
          dataSource={tableData}
          columns={columns}
          pagination={false}
          onRow={(row) => ({
            onClick: () => setEditor({ visible: true, type: row.type, record: row.record || null })
          })}
        />
      )}

      <SemestralDrawer
        visible={editor.visible && !['CLINICA','PSIQUIATRICA'].includes(editor.type)}
        onClose={(ok) => { setEditor({ visible: false, type: null, record: null }); if (ok) { load(); refresh && refresh(); } }}
        type={editor.type}
        record={editor.record}
        onSave={(data) => handleSave(editor.type, data, editor.record)}
        disabled={!canEdit || (editor.record && editor.record.completed_at)}
      />
      {editor.type === 'CLINICA' && (
        <SemestralClinicalPAIDrawer
          visible={editor.visible}
          onClose={(ok) => { setEditor({ visible: false, type: null, record: null }); if (ok) { load(); refresh && refresh(); } }}
          record={editor.record}
          onSave={(data) => handleSave('CLINICA', data, editor.record)}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
        />
      )}
      {editor.type === 'PSIQUIATRICA' && (
        <SemestralPsychiatricPAIDrawer
          visible={editor.visible}
          onClose={(ok) => { setEditor({ visible: false, type: null, record: null }); if (ok) { load(); refresh && refresh(); } }}
          record={editor.record}
          onSave={(data) => handleSave('PSIQUIATRICA', data, editor.record)}
          disabled={!canEdit || (editor.record && editor.record.completed_at)}
        />
      )}
    </Spin>
  );
}

export default function VGIContainer({ pacientId, hospitalizationStatus, isReadOnly }) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [refreshChildren, setRefreshChildren] = useState(0);
  const [viewMode, setViewMode] = useState('table'); // 'cards' | 'table' — default: table

  const isHospitalizationActive = hospitalizationStatus === 'A' && !isReadOnly;

  const load = () => {
    if (!pacientId) return;
    setLoading(true);
    reqwest({ url: `${config.api_url}/vgi/?pacient=${pacientId}`, method: 'get', type: 'json', headers: { 'Authorization': `Token ${getToken()}` } })
      .then((resp) => {
        const results = resp.results || resp || [];
        setList(results);
        // seleccionar la más reciente abierta si hay
        const open = results.find(x => !x.closed_at) || results[0] || null;
        setSelected(open);
      })
      .always(() => setLoading(false));
  };

  useEffect(() => { load(); }, [pacientId]);

  const createVGI = async () => {
    if (!isHospitalizationActive) return;
    Modal.confirm({
      title: 'Crear nueva VGI',
      content: 'Se creará una nueva VGI para este paciente.',
      onOk: async () => {
        await reqwest({ url: `${config.api_url}/vgi/`, method: 'post', type: 'json', contentType: 'application/json', headers: { 'Authorization': `Token ${getToken()}` }, data: JSON.stringify({ pacient: pacientId }) });
        message.success('VGI creada');
        load();
      }
    });
  };

  const closeVGI = async (record) => {
    if (!isHospitalizationActive) return;
    Modal.confirm({
      title: 'Cerrar VGI',
      content: 'Al cerrar la VGI no podrá editarse más. ¿Desea continuar?',
      onOk: async () => {
        await reqwest({ url: `${config.api_url}/vgi/${record.id}/`, method: 'patch', type: 'json', contentType: 'application/json', headers: { 'Authorization': `Token ${getToken()}` }, data: JSON.stringify({ closed_at: new Date().toISOString() }) });
        message.success('VGI cerrada');
        load();
      }
    });
  };

  const columns = useMemo(() => ([
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, it) => it.closed_at ? <Tag color="red">Cerrada</Tag> : <Tag color="green">Abierta</Tag>,
      width: 120,
    },
    {
      title: 'Creada',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v) => v ? new Date(v).toLocaleString() : '-',
    },
    {
      title: 'Actualizada',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (v) => v ? new Date(v).toLocaleString() : '-',
    },
    {
      title: 'Creado por',
      dataIndex: 'created_by_username',
      key: 'created_by_username',
      render: (v) => v || '-',
    },
    {
      title: 'Actualizado por',
      dataIndex: 'updated_by_username',
      key: 'updated_by_username',
      render: (v) => v || '-',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, it) => (
        <Space>
          {!it.closed_at && (
            <Button type="link" danger onClick={() => closeVGI(it)} disabled={!isHospitalizationActive}>Cerrar</Button>
          )}
        </Space>
      )
    }
  ]), []);

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={createVGI} disabled={!isHospitalizationActive}>Nueva VGI</Button>
            <Button onClick={load}>Refrescar</Button>
          </Space>
        </Col>
        <Col>
          <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
            <Radio.Button value="cards">Tarjetas</Radio.Button>
            <Radio.Button value="table">Tabla</Radio.Button>
          </Radio.Group>
        </Col>
      </Row>
      <Spin spinning={loading}>
        {(!list || list.length === 0) && <Empty description="No hay VGI para este paciente" />}
        {list && list.length > 0 && (
          viewMode === 'cards' ? (
            <Row gutter={[12, 12]}>
              {list.map((it) => (
                <Col xs={24} md={12} lg={8} key={it.id}>
                  <Card
                    hoverable
                    onClick={() => { setSelected(it); setDrawer(true); }}
                    size="small"
                    title={`VGI #${it.id}`}
                    extra={it.closed_at ? <Tag color="red">Cerrada</Tag> : <Tag color="green">Abierta</Tag>}
                  >
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label="Creada">{new Date(it.created_at).toLocaleString()}</Descriptions.Item>
                      <Descriptions.Item label="Actualizada">{new Date(it.updated_at).toLocaleString()}</Descriptions.Item>
                      <Descriptions.Item label="Creado por">{it.created_by_username || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Actualizado por">{it.updated_by_username || '-'}</Descriptions.Item>
                    </Descriptions>
                    <Space>
                      {!it.closed_at && <Button type="link" danger onClick={(e) => { e.stopPropagation(); closeVGI(it); }} disabled={!isHospitalizationActive}>Cerrar</Button>}
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Table
              size="small"
              rowKey={(r) => r.id}
              dataSource={list}
              onRow={(record) => ({
                onClick: () => { setSelected(record); setDrawer(true); }
              })}
              columns={columns}
              pagination={{ pageSize: 8 }}
            />
          )
        )}
      </Spin>

      <Drawer width={900} title={selected ? `VGI #${selected.id}` : 'VGI'} visible={drawer} onClose={() => setDrawer(false)} destroyOnClose>
        {selected && (
          <Tabs defaultActiveKey="valoraciones">
            <Tabs.TabPane tab="Valoraciones" key="valoraciones">
              <Row justify="end" style={{ marginBottom: 8 }}>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => {
                    const base = (config.api_url || '').replace(/\/$/, '');
                    window.open(`${base}/print/vgi/${selected.id}/valoraciones.pdf`, '_blank', 'noopener');
                  }}
                >
                  Imprimir
                </Button>
              </Row>
              <ValoracionesList vgi={selected} refresh={refreshChildren} viewMode={viewMode} hospitalizationStatus={hospitalizationStatus} isReadOnly={isReadOnly} />
            </Tabs.TabPane>
            <Tabs.TabPane tab="PAI" key="pai">
              <Row justify="end" style={{ marginBottom: 8 }}>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => {
                    const base = (config.api_url || '').replace(/\/$/, '');
                    window.open(`${base}/print/vgi/${selected.id}/pai.pdf`, '_blank', 'noopener');
                  }}
                >
                  Imprimir
                </Button>
              </Row>
              <PAIList vgi={selected} refresh={refreshChildren} viewMode={viewMode} hospitalizationStatus={hospitalizationStatus} isReadOnly={isReadOnly} />
            </Tabs.TabPane>
            <Tabs.TabPane tab="PAI (actualización semestral)" key="pai_semestral">
              <Row justify="end" style={{ marginBottom: 8 }}>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => {
                    const base = (config.api_url || '').replace(/\/$/, '');
                    window.open(`${base}/print/vgi/${selected.id}/pai-semestral.pdf`, '_blank', 'noopener');
                  }}
                >
                  Imprimir
                </Button>
              </Row>
              <PAIUpdateList vgi={selected} refresh={refreshChildren} viewMode={viewMode} hospitalizationStatus={hospitalizationStatus} isReadOnly={isReadOnly} />
            </Tabs.TabPane>
          </Tabs>
        )}
      </Drawer>
    </div>
  );
}
